import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Moon } from "lucide-react";
import PublicShell, { WIDE } from "@/components/PublicShell";
import { api } from "@/lib/apiClient";
import { usePageMeta } from "@/lib/seo";

export const MEANING = {
  new: "A fresh start. Set intentions, begin projects and plant seeds for the month ahead.",
  first_quarter: "A push to act. Obstacles show up to test your intentions; keep going and adjust.",
  full: "A peak of energy and emotion. Things come to light, results show and it's time to celebrate or release.",
  last_quarter: "A time to let go. Review, forgive, clear space and rest before the next cycle.",
};
const GLYPH = { new: "●", first_quarter: "◐", full: "○", last_quarter: "◑" };
const monthName = (ym) => new Date(`${ym}-15T12:00:00Z`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
const when = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" });

function MonthNav({ months, ym }) {
  const i = months.indexOf(ym);
  const btn = "flex items-center gap-1 text-sm gold-text";
  return (
    <nav aria-label="Moon calendar months" className="flex justify-between mt-8" data-testid="moon-month-nav">
      {i > 0 ? <Link to={`/moon-calendar/${months[i - 1]}`} className={btn} data-testid="moon-prev"><ChevronLeft size={15} /> {monthName(months[i - 1])}</Link> : <span />}
      {i < months.length - 1 ? <Link to={`/moon-calendar/${months[i + 1]}`} className={btn} data-testid="moon-next">{monthName(months[i + 1])} <ChevronRight size={15} /></Link> : <span />}
    </nav>
  );
}

export default function MoonCalendar() {
  const { ym } = useParams();
  const [months, setMonths] = useState(null);
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const month = ym || months?.[0];
  usePageMeta(month ? `Moon Phase Calendar ${monthName(month)}: New & Full Moon Dates · Astra` : "Moon Phase Calendar · Astra",
    ym ? `/moon-calendar/${ym}` : "/moon-calendar",
    month && `Every New Moon, Full Moon and quarter Moon in ${monthName(month)}, with exact times and zodiac signs, calculated from the real sky.`);
  useEffect(() => { api.get("/public/moon-calendar").then(({ data }) => setMonths(data.months)).catch(() => setError(true)); }, []);
  useEffect(() => {
    if (!month || (months && !months.includes(month))) return;
    setD(null); setError(false);
    api.get(`/public/moon-calendar/${month}`).then(({ data }) => setD(data)).catch(() => setError(true));
  }, [month, months]);

  if (months && ym && !months.includes(ym)) return <Navigate replace to="/moon-calendar" />;
  return (
    <PublicShell testId="moon-calendar">
      <div className={`px-5 pt-6 pb-10 fade-up ${WIDE ? "lg:max-w-3xl lg:mx-auto lg:pt-12" : ""}`}>
        <p className="text-[11px] uppercase tracking-[0.3em] gold-text"><Moon size={11} className="inline -mt-0.5 mr-1" />Moon phase calendar</p>
        <h1 data-testid="moon-title" className="text-4xl sm:text-5xl font-light mt-2 mb-3">Moon phases{month ? ` in ${monthName(month)}` : ""}</h1>
        <p className="text-base text-[#A7B0C0] mb-6">Exact times are shown in your local time, with the zodiac sign the Moon is in at that moment.</p>
        {error && <p data-testid="moon-error" className="text-[#A7B0C0]">The calendar is taking a moment to load. Please refresh in a minute.</p>}
        {!d && !error && <div className="flex justify-center py-12"><Loader2 className="animate-spin gold-text" size={26} /></div>}
        {d && (
          <ol className="space-y-3" data-testid="moon-phase-list">
            {d.phases.map((p) => (
              <li key={p.at} data-testid={`moon-phase-${p.phase}-${p.at.slice(0, 10)}`} className="bg-[#0F1523]/90 border border-[#D4AF37]/25 rounded-2xl p-5">
                <p className="text-xl font-light"><span className="gold-text mr-2">{GLYPH[p.phase]}</span>{p.label} in {p.sign}</p>
                <p className="text-sm text-[#E8DCC4] mt-0.5">{when(p.at)} · {p.degree}° {p.sign}</p>
                <p className="text-sm text-[#A7B0C0] mt-2">{MEANING[p.phase]}</p>
              </li>
            ))}
          </ol>
        )}
        {months && month && <MonthNav months={months} ym={month} />}
        <p className="text-xs text-[#A7B0C0] mt-6">Calculated with the PyEphem ephemeris (tropical zodiac). See <Link to="/transits/moon" className="gold-text">where the Moon is today</Link>.</p>
      </div>
    </PublicShell>
  );
}
